import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Link } from "react-router-dom";

interface BlogPost {
    title: string;
    date: string;
    tags: string[];
    content: string;
}

const extractFrontmatter = (markdown: string) => {
    const frontmatterRegex = /^---\n([\s\S]*?)\n---/;
    const match = markdown.match(frontmatterRegex);

    if (match && match[1]) {
        const frontmatter = match[1]
            .split("\n")
            .reduce((acc: { [key: string]: any }, line: string) => {
                const [key, value] = line.split(": ");
                if (key && value) {
                    try {
                        acc[key.trim()] = JSON.parse(value.trim());
                    } catch {
                        acc[key.trim()] = value.trim();
                    }
                }
                return acc;
            }, {});
        return {
            data: frontmatter,
            content: markdown.replace(frontmatterRegex, "").trim(),
        };
    }
    return { data: {}, content: markdown };
};

const Blog: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [post, setPost] = useState<BlogPost | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchBlogPost = async () => {
            setLoading(true);
            setError(null);
            try {
                const blogPostContext = (require as any).context("../content/blogs", false, /\.md$/);
                const fileUrl = blogPostContext(`./${slug}.md`);

                if (!fileUrl) {
                    throw new Error(`Blog post not found: ${slug}`);
                }

                const response = await fetch(fileUrl);
                const rawText = await response.text();
                const { data, content } = extractFrontmatter(rawText);

                setPost({
                    title: data.title,
                    date: data.date,
                    tags: data.tags as string[],
                    content,
                });
            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchBlogPost();
        }
    }, [slug]);

    if (loading) {
        return <div className="container mx-auto p-4 text-center">Loading blog post...</div>;
    }

    if (error) {
        return <div className="container mx-auto p-4 text-red-500 text-center">Error: {error}</div>;
    }

    if (!post) {
        return <div className="container mx-auto p-4 text-center">Blog post not found.</div>;
    }

    const wordsPerMinute = 200;
    const readingTime = Math.ceil(post.content.split(/\s+/).length / wordsPerMinute);

    return (
        <div className="container mx-auto p-4 max-w-4xl pt-24">
            <div className="mb-8">
                <Link to="/blogs" className="text-terminal-text hover:text-terminal-accent transition-colors duration-300 font-mono">
                    {">"} cd ../blogs
                </Link>
            </div>

            <header className="mb-12 border-b border-terminal-subtle pb-8">
                <h1 className="text-5xl font-bold mb-4 text-terminal-accent">{post.title}</h1>
                <div className="flex flex-wrap items-center gap-4 text-terminal-subtle font-mono text-sm">
                    <p>{new Date(post.date).toLocaleDateString()}</p>
                    <span>|</span>
                    <p>⏳ {readingTime} min read</p>
                    <span>|</span>
                    <div className="flex flex-wrap gap-2">
                        {post.tags.map((tag: string) => (
                            <span key={tag} className="text-terminal-text">
                                #{tag}
                            </span>
                        ))}
                    </div>
                </div>
            </header>

            <article className="prose prose-invert lg:prose-xl max-w-none prose-pre:bg-transparent prose-pre:p-0">
                <ReactMarkdown
                    remarkPlugins={[remarkGfm as any, remarkMath as any]}
                    rehypePlugins={[rehypeKatex as any]}
                    components={{
                        code({ node, inline, className, children, ...props }: any) {
                            const match = /language-(\w+)/.exec(className || "");
                            return !inline && match ? (
                                <SyntaxHighlighter
                                    style={atomDark}
                                    language={match[1]}
                                    PreTag="div"
                                    {...props}
                                >
                                    {String(children).replace(/\n$/, "")}
                                </SyntaxHighlighter>
                            ) : (
                                <code className={className} {...props}>
                                    {children}
                                </code>
                            );
                        },
                    }}
                >
                    {post.content}
                </ReactMarkdown>
            </article>
        </div>
    );
};

export default Blog;
